import { Divider } from '@design-system';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import { WORK_PAGE_TYPE } from '../../../../constants';
import { useCalculateOrderPricing } from '../../../../hooks/useCalculateOrderPricing';
import { GeneratedCalculateOrderPricingInputItem } from '../../../../types/generated';
import type { ProductPurchaseDetails } from '../../../../types/Purchasing';
import Drawer from '../../../Layout/Drawer';
import { ProductHelp } from '../ProductHelp';
import type { ProductDetailsProps } from '../types';
import CountryPicker from './CountryPicker';
import PriceBreakdown from './PriceBreakdown';
import WorkPreview from './WorkPreview';

interface ProductFeesDrawerProps<T extends ProductPurchaseDetails>
  extends ProductDetailsProps<T> {
  shown: boolean;
  onClose: () => void;
}

export const ProductFeesDrawer = <T extends ProductPurchaseDetails>({
  onClose,
  product,
  purchasing,
  shown,
}: ProductFeesDrawerProps<T>) => {
  const { t } = useTranslation('product');
  const { frame, product: productPurchasing, quantity } = purchasing;

  const artists = (() => {
    if (!product.productArtist) return [];

    return product.productArtist.map((a: ResolvedRelatedArtist) => a.artist);
  })();

  const [itemsToBuy, setItemsToBuy] = useState<
    GeneratedCalculateOrderPricingInputItem[]
  >([]);

  const { calculation: orderPriceCalculation } = useCalculateOrderPricing({
    items: itemsToBuy,
  });

  useEffect(() => {
    if (!productPurchasing) return;

    const items = [];
    items.push({
      variantId: productPurchasing.shopifyVariantId!,
      quantity,
    });

    if (frame) {
      frame.variant.consolidatedVariants.forEach((consolidatedVariant) => {
        items.push({
          variantId: consolidatedVariant.shopifyFrameVariantId,
          quantity: consolidatedVariant.quantity,
        });
      });
    }

    setItemsToBuy(items);
  }, [productPurchasing, frame, quantity]);

  return (
    <Drawer onClose={onClose} shown={shown}>
      {shown && (
        <section className="flex flex-col space-y-6">
          <div className="flex flex-col gap-y-1">
            <p className="text-md font-bold md:text-lg">
              {t('drawers.fees.title')}
            </p>
            <p>{t('drawers.fees.subTitle')}</p>
          </div>

          <CountryPicker
            artists={artists}
            product={product}
            purchasing={purchasing}
          />

          <WorkPreview
            artists={artists}
            product={product}
            purchasing={purchasing}
          />

          <Divider className="my-3" />

          <PriceBreakdown
            orderPriceCalculation={orderPriceCalculation}
            purchasing={purchasing}
          />

          <Divider className="my-3" />

          <ProductHelp
            analytics={{ page: WORK_PAGE_TYPE, context: 'fee drawer' }}
            onClose={onClose}
            orderAndShippingHelp
          />
        </section>
      )}
    </Drawer>
  );
};

export default ProductFeesDrawer;
